import cn from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { Loader, Typography } from '@pw-tech/omni-ui'
import InfinityCardItem from './InfinityCardItem'
import style from './index.module.css'
import { InfinityCardProps } from '../../utils/types'
import { PlanVariant } from '../../utils/enum'
import {
  CheckCircleIcon,
  formatCurrency,
  getCardStyleByPlan,
  PopularTagIcon,
  RecommendedTagIcon,
} from '../../utils/utilities'

const InfinityCard: React.FC<InfinityCardProps> = ({
  variant,
  index,
  data,
  price,
  total,
  discount,
  planName,
  planId,
  planVideoId,
  isRecommended,
  selectedPlan,
  isSinglePlan,
  planBadges,
  isProPlan,
  courseId,
  handlePlanSelect,
  planClicked,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [containerWidth, setContainerWidth] = useState<number | undefined>(
    undefined
  )

  // Updates container width on mount and when container changes
  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth)
    }
  }, [containerRef])

  // Utility function for getting background and border classes
  const getCardClasses = () => {
    const computedVariant =
      index === 0
        ? PlanVariant.BASIC
        : index === 1
        ? PlanVariant.INFINITY
        : PlanVariant.INFINITY_PRO
    const planStyle = getCardStyleByPlan(computedVariant, isProPlan)

    return cn(style.cardBackground, planStyle?.background, planStyle?.border, {
      [style.Opacity0]: selectedPlan?.planId !== planId,
      [style.Opacity100]: selectedPlan?.planId === planId,
    })
  }

  const FeaturesList = () => {
    const featuresListParentClass = cn(
      isSinglePlan ? style.singleCardSection : style.multipleCardSection
    )
    return (
      <div className={featuresListParentClass} ref={containerRef}>
        {data.map((el, index) => (
          <InfinityCardItem
            key={index}
            itemData={el}
            index={index}
            variant={variant}
            isRecommended={isSinglePlan ? true : isRecommended}
            containerWidth={containerWidth}
            isSinglePlan={isSinglePlan}
            isProPlan={isProPlan}
          />
        ))}
      </div>
    )
  }

  if (isSinglePlan) {
    return <FeaturesList />
  }

  const planDataPayload = {
    price,
    total,
    discount,
    planName,
    planId,
    planVideoId,
    isRecommended,
    courseId,
  }

  return (
    <div
      className={style.card}
      ref={containerRef}
      role="button"
      onClick={() => {
        if (!planClicked) {
          handlePlanSelect(planDataPayload)
        }
      }}
    >
      {/* Card Background */}
      <div className={getCardClasses()}></div>

      <div>
        {/* Top section with plan name and total price */}
        <div className={style.topSection}>
          <Typography component="div" color="static-white" variant="heading4">
            {planName?.slice(0, 20)}
          </Typography>

          <Typography
            component="div"
            color="static-white"
            variant="heading3"
            weight="bold"
          >
            {formatCurrency(total)}
          </Typography>
        </div>

        {/* Feature list */}
        <FeaturesList />
      </div>

      {/* Select button */}
      <div className={style.selectBtn}>
        {planClicked && selectedPlan?.planId === planId && (
          <Loader size="extra-small" />
        )}
        Select
      </div>

      {/* Recommended tag */}
      {planBadges === 'Recommended' && (
        <img
          src={RecommendedTagIcon}
          alt="Recommended"
          className={style.recommendedTag}
        />
      )}

      {/* Popular tag */}
      {planBadges === 'Popular' && (
        <img src={PopularTagIcon} alt="Popular" className={style.popularTag} />
      )}

      {/* Checkmark Icon */}
      {selectedPlan?.planId === planId && (
        <img
          src={CheckCircleIcon}
          alt="Check Circle"
          className={style.checkedCircle}
        />
      )}
    </div>
  )
}

export default InfinityCard
